import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Crown, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/** Strike 2 — Workspace branding + (admin) create new agency workspaces. */
export default function WorkspaceSettings() {
  const {
    workspaces,
    activeWorkspace,
    refreshWorkspaces,
    isAdmin,
    switchWorkspace,
  } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    logo_url: "",
    primary_color: "#6366f1",
    accent_color: "#0a0a0a",
  });
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (activeWorkspace) {
      const b = activeWorkspace.branding || {};
      setForm({
        name: activeWorkspace.name || "",
        company_name: b.company_name || "",
        logo_url: b.logo_url || "",
        primary_color: b.primary_color || "#6366f1",
        accent_color: b.accent_color || "#0a0a0a",
      });
    }
  }, [activeWorkspace]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!activeWorkspace) return;
    setSaving(true);
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}`, {
        name: form.name,
        branding: {
          company_name: form.company_name,
          logo_url: form.logo_url,
          primary_color: form.primary_color,
          accent_color: form.accent_color,
        },
      });
      toast.success("Workspace saved");
      await refreshWorkspaces();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    if (!newName.trim()) return toast.error("Name the workspace");
    setCreating(true);
    try {
      const r = await api.post("/workspaces", { name: newName.trim() });
      toast.success("Workspace created");
      await refreshWorkspaces();
      setNewName("");
      switchWorkspace(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
          Multi-tenant
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
          Workspace settings
        </h1>
      </div>

      {/* Active workspace branding */}
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
          <Building2 className="w-3.5 h-3.5" strokeWidth={1.8} />
          {activeWorkspace?.name || "Workspace"} — branding
        </div>
        <Field label="Workspace name">
          <input
            value={form.name}
            onChange={(e) => upd({ name: e.target.value })}
            data-testid="ws-name-input"
            className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Company name">
          <input
            value={form.company_name}
            onChange={(e) => upd({ company_name: e.target.value })}
            data-testid="ws-company-input"
            className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Logo URL">
          <input
            value={form.logo_url}
            onChange={(e) => upd({ logo_url: e.target.value })}
            placeholder="https://…"
            data-testid="ws-logo-input"
            className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Brand colors">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-neutral-400">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => upd({ primary_color: e.target.value })}
                data-testid="ws-primary-color"
                className="w-9 h-9 rounded bg-transparent border border-[#27272a] cursor-pointer"
              />
              <span className="font-mono">{form.primary_color}</span>
            </label>
            <label className="flex items-center gap-2 text-xs text-neutral-400">
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => upd({ accent_color: e.target.value })}
                data-testid="ws-accent-color"
                className="w-9 h-9 rounded bg-transparent border border-[#27272a] cursor-pointer"
              />
              <span className="font-mono">{form.accent_color}</span>
            </label>
          </div>
        </Field>
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="ws-save-button"
            className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" strokeWidth={1.8} />
            )}
            Save branding
          </button>
        </div>
      </div>

      {/* All workspaces */}
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-3">
          Your workspaces ({workspaces.length})
        </div>
        <div className="space-y-1">
          {workspaces.map((w) => (
            <div
              key={w.id}
              className="flex items-center justify-between px-3 py-2 rounded-md border border-[#161616] bg-[#0a0a0a]"
            >
              <span className="text-sm">
                {w.name}
                {w.is_default && (
                  <span className="ml-2 text-[9px] font-mono uppercase tracking-[0.15em] text-neutral-600">
                    default
                  </span>
                )}
              </span>
              {w.id !== activeWorkspace?.id && (
                <button
                  onClick={() => switchWorkspace(w.id)}
                  className="text-xs font-mono text-neutral-400 hover:text-white"
                >
                  switch
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Create new (admin only) */}
      {isAdmin ? (
        <div
          className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6"
          data-testid="ws-create-card"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-3">
            New agency workspace
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Client / brand name"
              data-testid="ws-create-input"
              className="flex-1 bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
            />
            <button
              onClick={create}
              disabled={creating}
              data-testid="ws-create-button"
              className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" strokeWidth={1.8} />
              )}
              Create
            </button>
          </div>
        </div>
      ) : (
        <div
          className="border border-amber-500/20 bg-amber-500/5 rounded-lg p-6"
          data-testid="ws-enterprise-card"
        >
          <div className="flex items-center gap-2 text-amber-300">
            <Crown className="w-4 h-4" strokeWidth={1.8} />
            <span className="text-xs font-mono uppercase tracking-[0.2em]">
              Enterprise feature
            </span>
          </div>
          <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
            Creating additional agency workspaces (sub-accounts) is an Enterprise
            capability. To unlock multi-workspace management, contact{" "}
            <a
              href="mailto:yasen@coreflowconsulting.com"
              data-testid="ws-enterprise-contact"
              className="text-amber-300 hover:text-amber-200 font-mono"
            >
              yasen@coreflowconsulting.com
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
      <div className="text-sm text-neutral-400 sm:w-40">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
